// UI strings of the rebuilt Kääntäjä screen (layout v2, docs/design/SIVUPALSTA.md "Kääntäjä"): the
// one direction control, the "Käännösasetukset" sheet, the actions after the output (Tallenna ·
// Oma käännös · Lisää), the history page and the margin's notes on unsure words. Finnish per
// docs/FINNISH-STYLE.md; nothing is truncated.
import { defineArea } from '../area';

type Lang3 = 'hv' | 'fi' | 'en';

export const trScreenFi = {
  /** The direction control, read as "valyria → suomi". */
  lang: { hv: 'valyria', fi: 'suomi', en: 'englanti' } as Record<Lang3, string>,
  dirAria: (from: string, to: string) => `Käännössuunta: ${from} → ${to}`,
  swap: 'Vaihda suunta',
  settings: 'Käännösasetukset',
  /** The sheet: the language on the other side of High Valyrian. */
  otherLang: 'Toinen kieli',
  otherLangs: { fi: 'Suomi', en: 'Englanti' } as Record<'fi' | 'en', string>,
  /** The settings in one line (PC margin, the button's description). */
  summary: (lang: string, letter: boolean, tone: string | null) => [lang, letter ? 'kirje' : null, tone].filter(Boolean).join(' · '),
  change: 'Muuta',
  // ---- after the output ----
  own: 'Oma käännös',
  ownTitle: 'Oma käännöksesi',
  ownIntro: 'Kirjoita käännös niin kuin sen pitäisi olla. Se menee tarkistusjonoon, ja kun se on hyväksytty, kääntäjä käyttää sitä.',
  ownNoteOptional: 'Huomautus (valinnainen)',
  ownNeedsChange: 'Muuta käännöstä ensin: nyt se on sama kuin kääntäjän.',
  ownNote: 'Oma käännös.',
  more: 'Lisää',
  moreTitle: 'Lisää toimintoja',
  // ---- history ----
  history: 'Äskeiset ja tallennetut',
  historyAll: 'Kaikki käännökset',
  historyTitle: 'Käännökset',
  recentNone: 'Ei vielä käännöksiä.',
  // ---- the margin: unsure words (≥ 1600px, lane A) ----
  unsure: 'Epävarma sana',
  why: {
    unknown: 'Sovellus ei tunnista tätä sanaa.',
    foreign: 'Sanalle ei löytynyt valyrian vastinetta.',
    fallback: 'Suomenkielistä merkitystä ei ole; merkitys on englanniksi.',
    readings: (n: number) => `${n} mahdollista tulkintaa; kääntäjä valitsi yhden.`,
  },
  openWord: 'Avaa sana',
  // ---- the answer first (fix batch D2, 2026-10-06): the translation and its confidence under Käännä ----
  /** The answer's label by its language ("suomeksi" / "englanniksi" / "valyriaksi"). */
  answer: { hv: 'valyriaksi', fi: 'suomeksi', en: 'englanniksi' } as Record<Lang3, string>,
  /** Joins the confidence line's last two reasons ("… ja …"). */
  and: 'ja',
};

export const trScreenEn: typeof trScreenFi = {
  lang: { hv: 'Valyrian', fi: 'Finnish', en: 'English' },
  dirAria: (from, to) => `Direction: ${from} → ${to}`,
  swap: 'Swap direction',
  settings: 'Translation settings',
  otherLang: 'Other language',
  otherLangs: { fi: 'Finnish', en: 'English' },
  summary: (lang, letter, tone) => [lang, letter ? 'letter' : null, tone].filter(Boolean).join(' · '),
  change: 'Change',
  own: 'Own translation',
  ownTitle: 'Your own translation',
  ownIntro: 'Write the translation as it should be. It goes to the review queue, and once approved the translator uses it.',
  ownNoteOptional: 'Note (optional)',
  ownNeedsChange: 'Change the translation first: now it is the same as the translator’s.',
  ownNote: 'Own translation.',
  more: 'More',
  moreTitle: 'More actions',
  history: 'Recent and saved',
  historyAll: 'All translations',
  historyTitle: 'Translations',
  recentNone: 'No translations yet.',
  unsure: 'Unsure word',
  why: {
    unknown: 'The app does not recognise this word.',
    foreign: 'No Valyrian word was found for it.',
    fallback: 'No Finnish meaning; the meaning is in English.',
    readings: (n) => `${n} possible readings; the translator chose one.`,
  },
  openWord: 'Open the word',
  answer: { hv: 'in Valyrian', fi: 'in Finnish', en: 'in English' },
  and: 'and',
};

// Messages.trScreen (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('trScreen', trScreenFi, trScreenEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof trScreenFi> {}
}
